import {Link as RouterLink, useNavigate} from 'react-router-dom';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import TollOutlinedIcon from '@mui/icons-material/TollOutlined';
import {Box, Button, IconButton, Link, Tooltip} from '@mui/material';
import {MRT_ColumnDef, MRT_SortingState} from 'material-react-table';

import {
  getUserCurrency,
  SortOrder,
  useGetUsers,
  User,
  UserAvatar,
  UserSortField,
  useSetAdmin,
  useTopUpCoins,
  useTopUpPromo,
  useTopUpReal,
} from '@entities/user';
import {
  CurrencyValue,
  MrTable,
  rightAlignmentColumnProps,
  TableRowActionsContainer,
} from '@shared/components';
import {FilterRow, SearchFilter} from '@shared/components/filters';
import {
  DATETIME_FORMAT,
  entityTableCommonProps,
  ROUTE__USER_ACCOUNT,
  ROUTE__USER_ACCOUNT__ORDERS,
} from '@shared/constants';
import {useUrlState} from '@shared/hooks';
import {formatInTz} from '@shared/utils';
import {
  openTopUpCoinsDialog,
  openTopUpPromoDialog,
  openTopUpRealDialog,
} from '@widgets/user';

import {PaginationState} from '@/shared/types';

// Only the columns mapped here can be sorted (the backend supports these two)
const columnIdToSortField: Record<string, UserSortField> = {
  id: 'username',
  registeredAt: 'registered_at',
};

const sortFieldToColumnId: Record<UserSortField, string> = {
  username: 'id',
  registered_at: 'registeredAt',
};

const columns: MRT_ColumnDef<User>[] = [
  {
    accessorKey: 'id',
    header: 'ID',
    enableSorting: true,
    Cell: ({row}) => {
      const {id, username} = row.original;

      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Link
            component={RouterLink}
            to={ROUTE__USER_ACCOUNT.replace(':id', id)}
          >
            {username}
          </Link>
          <Tooltip title={id}>
            <IconButton
              size="small"
              onClick={() => void navigator.clipboard.writeText(id)}
            >
              <ContentCopyIcon sx={{fontSize: 14}} />
            </IconButton>
          </Tooltip>
        </Box>
      );
    },
  },
  {
    accessorKey: 'name',
    header: 'Name',
    grow: true,
  },
  {
    accessorKey: 'registeredAt',
    header: 'Registered',
    enableSorting: true,
    Cell: ({row}) =>
      row.original.registeredAt
        ? formatInTz(row.original.registeredAt, DATETIME_FORMAT)
        : '—',
    size: 160,
  },
  {
    accessorKey: 'phone',
    header: 'Phone',
    size: 140,
  },
  {
    accessorKey: 'avatar',
    header: 'Avatar',
    Cell: ({row}) => {
      const {name, avatar} = row.original;

      return <UserAvatar name={name} src={avatar} />;
    },
    size: 120,
  },
  {
    accessorKey: 'email',
    header: 'Email',
    size: 140,
    grow: true,
  },
  {
    accessorKey: 'country',
    header: 'Country',
    size: 120,
  },
  {accessorKey: 'authType', header: 'Auth type', size: 140},
  {
    id: 'ban',
    header: 'Banned',
    Cell: ({row}) => (row.original.ban ? 'Yes' : 'No'),
    size: 120,
  },
  {
    accessorKey: 'isVerified',
    header: 'Is verified?',
    Cell: ({row}) => {
      return row.original.isVerified ? 'Yes' : 'No';
    },
    size: 140,
  },
  {
    accessorKey: 'isAdmin',
    header: 'Is admin?',
    Cell: ({row}) => {
      return row.original.isAdmin ? 'Yes' : 'No';
    },
    size: 130,
  },
  {
    accessorKey: 'fundsReal',
    header: 'Funds real',
    Cell: ({row}) => {
      return <CurrencyValue value={row.original.fundsReal} />;
    },
    maxSize: 160,
    ...rightAlignmentColumnProps,
  },
  {
    accessorKey: 'fundsPromo',
    header: 'Funds promo',
    Cell: ({row}) => {
      return <CurrencyValue value={row.original.fundsPromo} />;
    },
    maxSize: 160,
    ...rightAlignmentColumnProps,
  },
];

const localStorageKey = 'userTable';

type UrlState = PaginationState & {
  searchQuery?: string;
  sortField: UserSortField;
  sortOrder: SortOrder;
};

export const UsersPage = () => {
  const navigate = useNavigate();

  const {urlState, setUrl} = useUrlState<UrlState>({
    pageIndex: 0,
    pageSize: 10,
    searchQuery: undefined,
    sortField: 'registered_at',
    sortOrder: 'desc',
  });

  const sorting: MRT_SortingState = [
    {
      id: sortFieldToColumnId[urlState.sortField],
      desc: urlState.sortOrder === 'desc',
    },
  ];

  const {
    data,
    isPending: isLoading,
    error,
  } = useGetUsers({
    page: urlState.pageIndex + 1,
    pageSize: urlState.pageSize,
    searchQuery: urlState.searchQuery,
    sortField: urlState.sortField,
    sortOrder: urlState.sortOrder,
  });
  const {mutate: setAdmin, isPending: isSettingAdmin} = useSetAdmin();
  const {mutate: topUpReal, isPending: isToppingUpReal} = useTopUpReal();
  const {mutate: topUpPromo, isPending: isToppingUpPromo} = useTopUpPromo();
  const {mutate: topUpCoins, isPending: isToppingUpCoins} = useTopUpCoins();

  return (
    <MrTable
      columns={columns}
      data={data?.items ?? []}
      error={error}
      renderRowActions={({row}) => {
        const {id, username, isAdmin} = row.original;

        return (
          <TableRowActionsContainer>
            <Button
              variant="text"
              color="secondary"
              onClick={() => {
                navigate(ROUTE__USER_ACCOUNT__ORDERS.replace(':id', id));
              }}
            >
              Orders
            </Button>

            <IconButton
              title={`${isAdmin ? 'Revoke' : 'Grant'} admin permissions`}
              disabled={isSettingAdmin}
              onClick={() => {
                setAdmin({userId: id, isAdmin: !isAdmin});
              }}
            >
              {isAdmin ? (
                <PersonRemoveOutlinedIcon />
              ) : (
                <PersonAddAltOutlinedIcon />
              )}
            </IconButton>

            <IconButton
              title="Top up real balance"
              disabled={isToppingUpReal}
              onClick={() => {
                void getUserCurrency(username)
                  .then(({currency}) =>
                    openTopUpRealDialog({
                      title: 'Top up real balance',
                      label: `Amount (in ${currency})`,
                    }),
                  )
                  .then((amount: number) => {
                    topUpReal({username, amount});
                    return;
                  })
                  .catch(() => {});
              }}
            >
              <AttachMoneyOutlinedIcon />
            </IconButton>

            <IconButton
              title="Top up promo balance"
              disabled={isToppingUpPromo}
              onClick={() => {
                void getUserCurrency(username)
                  .then(({currency}) =>
                    openTopUpPromoDialog({
                      title: 'Top up promo balance',
                      label: `Amount (in ${currency})`,
                    }),
                  )
                  .then((amount: number) => {
                    topUpPromo({username, amount});
                    return;
                  })
                  .catch(() => {});
              }}
            >
              <CardGiftcardOutlinedIcon />
            </IconButton>

            <IconButton
              title="Top up coins"
              disabled={isToppingUpCoins}
              onClick={() => {
                void openTopUpCoinsDialog({
                  title: 'Top up coins',
                  label: 'Amount (coins)',
                })
                  .then((coins: number) => {
                    topUpCoins({username, coins});
                    return;
                  })
                  .catch(() => {});
              }}
            >
              <TollOutlinedIcon />
            </IconButton>
          </TableRowActionsContainer>
        );
      }}
      renderTopToolbarCustomActions={() => (
        <Box display="flex" alignItems="center" width="100%">
          <FilterRow>
            <SearchFilter
              value={urlState.searchQuery}
              onChange={searchQuery =>
                setUrl((prev: UrlState) => ({
                  ...prev,
                  pageIndex: 0,
                  searchQuery,
                }))
              }
            />
          </FilterRow>
        </Box>
      )}
      enableColumnOrdering
      enableColumnDragging={false}
      localStorageKeyForSettings={localStorageKey}
      enableHiding
      /////////////////////////////
      // pagination + state
      enablePagination
      manualPagination
      onPaginationChange={updater =>
        setUrl((prev: UrlState) => {
          const nextPagination =
            typeof updater === 'function' ? updater(prev) : updater;

          return {...prev, ...nextPagination};
        })
      }
      rowCount={data?.total ?? 0}
      /////////////////////////////
      manualSorting
      defaultColumn={{enableSorting: false}}
      enableSortingRemoval={false}
      enableMultiSort={false}
      onSortingChange={updater => {
        const next = typeof updater === 'function' ? updater(sorting) : updater;
        const first = next[0];

        setUrl((prev: UrlState) => ({
          ...prev,
          pageIndex: 0,
          sortField: first
            ? (columnIdToSortField[first.id] ?? 'registered_at')
            : 'registered_at',
          sortOrder: first && !first.desc ? 'asc' : 'desc',
        }));
      }}
      state={{
        isLoading,
        pagination: urlState,
        sorting,
      }}
      enableColumnPinning
      // layoutMode="grid-no-grow"
      initialState={{
        columnPinning: {
          left: ['id'],
          right: ['mrt-row-actions'],
        },
      }}
      {...entityTableCommonProps}
    />
  );
};
